import React, { createContext, useContext, useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { DEFAULT_SITE_THEME, isSiteThemeId, type SiteThemeId } from "../../../shared/site-themes";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme?: () => void;
  switchable: boolean;
  siteTheme: SiteThemeId;
  setSiteTheme: (theme: SiteThemeId, tenantSlug?: string) => void;
  weatherEnabled: boolean;
  setWeatherEnabled: (enabled: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  switchable?: boolean;
}

export function ThemeProvider({ children, defaultTheme = "light", switchable = false }: ThemeProviderProps) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const preference = trpc.userThemePreference.get.useQuery(undefined, {
    enabled: switchable && Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1,
  });
  const savePreference = trpc.userThemePreference.set.useMutation();
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [siteTheme, setSiteThemeState] = useState<SiteThemeId>(DEFAULT_SITE_THEME);
  const [weatherEnabled, setWeatherEnabledState] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    if (!switchable || !user?.id) {
      setTheme(defaultTheme);
      setSiteThemeState(DEFAULT_SITE_THEME);
      setWeatherEnabledState(false);
      return;
    }
    if (!preference.isFetched) return;
    const savedSiteTheme = preference.data?.siteTheme;
    const nextSiteTheme = isSiteThemeId(savedSiteTheme) ? savedSiteTheme : DEFAULT_SITE_THEME;
    setSiteThemeState(nextSiteTheme);
    setTheme(nextSiteTheme === "dark-mode" || preference.data?.darkMode ? "dark" : "light");
  }, [defaultTheme, preference.data?.darkMode, preference.data?.siteTheme, preference.isFetched, switchable, user?.id]);

  useEffect(() => {
    document.documentElement.dataset.siteTheme = siteTheme;
  }, [siteTheme]);

  const setSiteTheme = (nextSiteTheme: SiteThemeId, tenantSlug?: string) => {
    if (!user?.id) return;
    const previousSiteTheme = siteTheme;
    const previousTheme = theme;
    const nextDarkMode = nextSiteTheme === "dark-mode";
    setSiteThemeState(nextSiteTheme);
    setTheme(nextDarkMode ? "dark" : "light");
    utils.userThemePreference.get.setData(undefined, (current) => current ? {
      ...current,
      darkMode: nextDarkMode,
      siteTheme: nextSiteTheme,
    } : {
      darkMode: nextDarkMode,
      siteTheme: nextSiteTheme,
      weatherEnabled: false,
      avatarPreset: null,
      avatarUrl: null,
    });
    savePreference.mutate(
      { siteTheme: nextSiteTheme, darkMode: nextDarkMode, tenantSlug },
      {
        onError: () => {
          setSiteThemeState(previousSiteTheme);
          setTheme(previousTheme);
          void utils.userThemePreference.get.invalidate();
        },
      },
    );
  };

  const setWeatherEnabled = (enabled: boolean) => {
    if (!user?.id) return;
    const previous = weatherEnabled;
    setWeatherEnabledState(enabled);
    utils.userThemePreference.get.setData(undefined, (current) => current ? { ...current, weatherEnabled: enabled } : current);
    savePreference.mutate(
      { weatherEnabled: enabled },
      {
        onError: () => {
          setWeatherEnabledState(previous);
          void utils.userThemePreference.get.invalidate();
        },
      },
    );
  };

  const toggleTheme = switchable && user?.id
    ? () => setSiteTheme(theme === "dark" ? DEFAULT_SITE_THEME : "dark-mode")
    : undefined;

  useEffect(() => {
    if (!switchable || !user?.id || !preference.isFetched) return;
    setWeatherEnabledState(preference.data?.weatherEnabled === true);
  }, [preference.data?.weatherEnabled, preference.isFetched, switchable, user?.id]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, switchable, siteTheme, setSiteTheme, weatherEnabled, setWeatherEnabled }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
}
